import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  resolved: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  resetToLight: () => void;
  toggle: () => void;
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
}

function applyTheme(resolved: 'light' | 'dark') {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      resolved: resolveTheme('light'),

      setTheme: (theme) => {
        const resolved = resolveTheme(theme);
        applyTheme(resolved);
        set({ theme, resolved });
      },

      resetToLight: () => {
        applyTheme('light');
        set({ theme: 'light', resolved: 'light' });
      },

      toggle: () => {
        const { resolved } = get();
        const next: Theme = resolved === 'dark' ? 'light' : 'dark';
        get().setTheme(next);
      },
    }),
    { name: 'theme-preference' }
  )
);

// Initialize on load — always start as light unless user explicitly chose dark
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('theme-preference');
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      const theme = parsed?.state?.theme || 'light';
      // Only restore dark if user explicitly chose it (not 'system')
      if (theme === 'dark') {
        useThemeStore.getState().setTheme('dark');
      } else {
        useThemeStore.getState().setTheme('light');
      }
    } catch {
      useThemeStore.getState().setTheme('light');
    }
  } else {
    // No stored preference → always light
    useThemeStore.getState().setTheme('light');
  }

  // Listen for OS preference changes (only relevant if user chose 'system')
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { theme } = useThemeStore.getState();
    if (theme === 'system') {
      useThemeStore.getState().setTheme('system');
    }
  });
}
